const INITIAL_THREAD_SELECTOR = "main[data-aimg-thread-initial]";
export const INITIAL_THREAD_CONTENT_ID = "initial-thread-content";
export const MAIN_CONTENT_ID = "main-content";

type RetainedInitialThreadHtml = {
  element: HTMLElement;
  root: HTMLElement;
};

let retained: RetainedInitialThreadHtml | null = null;
const listeners = new Set<() => void>();

/**
 * Keep the server-rendered thread outside React's mount point until the SPA has
 * committed an authoritative replacement.
 */
export function retainInitialThreadHtml(
  root: HTMLElement,
  shouldRetain = true,
): HTMLElement | null {
  if (retained?.root === root) {
    if (shouldRetain) return retained.element;
    discardInitialThreadHtml();
    return null;
  }
  discardInitialThreadHtml();
  const initial = [...root.children].find(
    (child): child is HTMLElement =>
      child instanceof HTMLElement && child.matches(INITIAL_THREAD_SELECTOR),
  );
  if (!initial) return null;
  if (!shouldRetain) {
    initial.remove();
    return null;
  }

  const region = root.ownerDocument.createElement("section");
  for (const attribute of initial.attributes) {
    region.setAttribute(attribute.name, attribute.value);
  }
  region.id = INITIAL_THREAD_CONTENT_ID;
  region.setAttribute("role", "region");
  region.tabIndex = -1;
  region.removeAttribute("hidden");
  region.append(...initial.childNodes);
  initial.replaceWith(region);
  root.before(region);
  retained = { element: region, root };
  emitChange();
  return region;
}

/** Remove the retained HTML after successful SPA rendering or a definitive 404. */
export function discardInitialThreadHtml(): void {
  if (!retained) return;
  retained?.element.remove();
  retained = null;
  emitChange();
}

export function getMainContentTargetId(): string {
  return retained === null ? MAIN_CONTENT_ID : INITIAL_THREAD_CONTENT_ID;
}

export function subscribeMainContentTarget(listener: () => void): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

/** Remove old thread HTML when SPA navigation leaves its original document URL. */
export function discardInitialThreadHtmlForNavigation(
  previousUrl: string | undefined,
  nextUrl: string,
): void {
  if (
    !retained ||
    previousUrl === undefined ||
    documentUrl(previousUrl) === documentUrl(nextUrl)
  ) {
    return;
  }
  discardInitialThreadHtml();
}

function documentUrl(url: string): string {
  const parsed = new URL(url, document.baseURI);
  parsed.hash = "";
  return parsed.href;
}

function emitChange(): void {
  for (const listener of listeners) listener();
}
